import React, { useState, useRef, useEffect, useCallback } from "react";
import "../../styles/WordReportingTool.css";
import axios from "axios";
import ReportingBottomToolbar from "../../components/ReportingBottomToolbar";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ReportDocument from "../../components/ReportDocument";
import {generateWordReport} from "../../utils/GenerateWordDocument"
// ─── Tool Config ─────────────────────────────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; 
export const WordReportToolConfig = {
  name: "Word Report",
  tool: "WordReportTool",
  icon: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  ),
};

// ─── Status constants ─────────────────────────────────────────────────────────
const STATUS = {
  OPEN:       "open",
  LOCKED:     "locked",
  SIGNED_OFF: "signed_off",
};

const STATUS_LABELS = {
  open:       "Open",
  locked:     "Locked",
  signed_off: "Signed Off",
};
const formats = [
  { value: "dicom", label: "DICOM" },
  { value: "jpeg", label: "JPEG" },
  { value: "png", label: "PNG" },
  { value: "pdf", label: "PDF" },
  { value: "zip", label: "ZIP" },
];
const STATUS_STYLES = {
  open:       { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7", icon: "🟢" },
  locked:     { bg: "#fff3e0", color: "#e65100", border: "#ffcc80", icon: "🔒" },
  signed_off: { bg: "#e3f2fd", color: "#0d47a1", border: "#90caf9", icon: "✅" },
};
// ─── execCmd helper ───────────────────────────────────────────────────────────
function RBtn({ title, onClick, active, children, wide }) {
  return (
    <button
      title={title}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick?.(e); }}
      className={`word-rbtn ${active ? "active" : ""} ${wide ? "wide" : ""}`}
    >
      {children}
    </button>
  );
}

function Sep() { return <div className="word-sep" />; }

function GroupLabel({ children }) { return <div className="word-group-label">{children}</div>; }

function RGroup({ label, children }) {
  return (
    <div className="word-rgroup">
      <div className="word-rgroup-buttons">{children}</div>
      <GroupLabel>{label}</GroupLabel>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function WordReportPopup({ patient, onClose, closeModel }) {
  const editorRef      = useRef(null);
  const savedSelRef    = useRef(null);
  const [loading,          setLoading]          = useState(false);
  const [error,            setError]            = useState("");
  const [zoom,             setZoom]             = useState(100);
  const [wordCount,        setWordCount]        = useState(0);
  const [activeTab,        setActiveTab]        = useState("Home");
  const [isFullscreen,     setIsFullscreen]     = useState(false);
  const [fmt,              setFmt]              = useState({ bold:false, italic:false, underline:false, strike:false, ul:false, ol:false, jL:true, jC:false, jR:false, jF:false });
  const [fontName,         setFontName]         = useState("Calibri");
  const [fontSize,         setFontSize]         = useState("11");
  const [showColorPicker,  setShowColorPicker]  = useState(false);
  const [showHLPicker,     setShowHLPicker]     = useState(false);
  const [textColor,        setTextColor]        = useState("#000000");
  const [hlColor,          setHlColor]          = useState("#FFFF00");

  // ── Status state ──────────────────────────────────────────────────────────
  const [reportStatus,     setReportStatus]     = useState(STATUS.OPEN);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusMsg,        setStatusMsg]        = useState("");
  const bottomToolbarRef = useRef(null);
  const p = patient || {};
  // ── Study ID helpers ──────────────────────────────────────────────────────
  const getStudyId = (pt) => {
    if (pt?.studyId)            return pt.studyId;
    if (pt?.study_id)           return pt.study_id;
    if (pt?.orthanc_study_id)   return pt.orthanc_study_id;
    if (pt?.studies?.length)    return pt.studies[0]?.study_id || pt.studies[0]?.id || "N/A";
    return "N/A";
  };
  const studyId      = getStudyId(p);
  const patientIdVal = p.patientId || p.patient_id || p.id || "unknown";

  // ── Patient details ───────────────────────────────────────────────────────
  const patientDetails = [
    { label: "Patient Name",        value: p.patientName        || p.patient_name        || "N/A" },
    { label: "Patient ID",          value: p.patientId          || p.patient_id          || "N/A" },
    { label: "Birth Date",          value: p.birthDate          || p.birth_date          || "N/A" },
    { label: "Age",                 value: p.age                                         || "N/A" },
    { label: "Gender",              value: p.gender                                      || "N/A" },
    { label: "Study ID",            value: studyId                                              },
    { label: "Study Date",          value: p.studyDate          || p.study_date          || "N/A" },
    { label: "Study Description",   value: p.studyDescription   || p.study_description   || "N/A" },
    { label: "Series Description",  value: p.seriesDescription  || p.series_description  || "N/A" },
    { label: "Accession No",        value: p.accessionNumber    || p.accession_no        || "N/A" },
    { label: "Modality",            value: p.modality                                    || "N/A" },
    { label: "Referring Physician", value: p.referring_physician || p.referring_physician || "N/A" },
  ];

  const mid          = Math.ceil(patientDetails.length / 2);
  const leftDetails  = patientDetails.slice(0, mid);
  const rightDetails = patientDetails.slice(mid);
  const [formats, setFormats] = useState([]);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState("");
  const [currentReportId, setCurrentReportId] = useState(null);
const [currentReportName, setCurrentReportName] = useState("");
  // ── Load status from backend on mount ────────────────────────────────────
  useEffect(() => {
    if (studyId && studyId !== "N/A") {
      fetch(`${API_BASE_URL}/api/study-status/${studyId}/`)
        .then(res => res.ok ? res.json() : null)
        .then(data => { 
          if (data?.report_status) {
            const cleanStatus = data.report_status.toLowerCase();
            if (cleanStatus == "locked") setReportStatus(STATUS.LOCKED);
            else if (cleanStatus === "signed_off") setReportStatus(STATUS.SIGNED_OFF);
            else setReportStatus(STATUS.OPEN); // Default fallback
          }
        })
        .catch(() => {
          setReportStatus(STATUS.OPEN); // Fallback on network failure
        });
    }
  }, [studyId]);

  useEffect(() => {
    // 1. Get the current logged-in doctor's ID (e.g., from localStorage or auth state)
    const loggedInUserString = localStorage.getItem('user'); 
    const loggedInUser = JSON.parse(loggedInUserString);
    const loggedInDoctorId = loggedInUser?.id; 

    axios.get(`${API_BASE_URL}/api/templates/`, {
            params: {
                template_type: "REPORT_FORMAT",
                doctor: loggedInDoctorId // 2. Pass the doctor ID into the query params
            },
        })
        .then((res) => {
          console.log(res,'response')
            setFormats(res.data);
        })
        .catch((err) => {
            console.error("Error fetching doctor templates:", err);
        });
}, []);
  const handleNewReportCreated = (report) => {
    setCurrentReportId(report.id);
    setCurrentReportName(report.report_name);
    setReportStatus(STATUS.OPEN);
    if (editorRef.current) {
        editorRef.current.innerHTML = "";
    }
  };
  const handleTemplateChange = (e) => {
    const id = e.target.value;
    setSelectedFormat(id);
    const template = formats.find(
        item => String(item.id) === String(id)
    );
    if (!template) return;
    if (editorRef.current) {
        if (currentReportId && editorRef.current.innerHTML.trim() !== "") {
            if (!window.confirm("Changing the format will clear your current report text. Proceed?")) {
                return;
            }
        }

        editorRef.current.innerHTML = template.content || "<p>No Content Found</p>";
        refreshFmt();
    }
  };
  const updateStudyStatus = async (newStatus) => {
    if (studyId === "N/A") return true;
    try {
      const res = await fetch(`${API_BASE_URL}/api/study-status/${studyId}/`, {
        method:  "PATCH",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ report_status: newStatus }),
      });
      return res.ok;
    } catch {
      return false;
    }
  };

  // ── Selection helpers ─────────────────────────────────────────────────────
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedSelRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (!savedSelRef.current) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedSelRef.current);
  };

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    restoreSelection();
    document.execCommand(cmd, false, val);
    refreshFmt();
  }, []);

  const refreshFmt = useCallback(() => {
    try {
      setFmt({
        bold:      document.queryCommandState("bold"),
        italic:    document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        strike:    document.queryCommandState("strikeThrough"),
        ul:        document.queryCommandState("insertUnorderedList"),
        ol:        document.queryCommandState("insertOrderedList"),
        jL:        document.queryCommandState("justifyLeft"),
        jC:        document.queryCommandState("justifyCenter"),
        jR:        document.queryCommandState("justifyRight"),
        jF:        document.queryCommandState("justifyFull"),
      });
      const fn = document.queryCommandValue("fontName").replace(/['"]/g,"");
      if (fn) setFontName(fn);
      const fs = document.queryCommandValue("fontSize");
      if (fs) { const m={"1":"8","2":"10","3":"12","4":"14","5":"18","6":"24","7":"36"}; setFontSize(m[fs]||fs); }
      const txt = editorRef.current?.innerText||"";
      setWordCount(txt.trim() ? txt.trim().split(/\s+/).length : 0);
    } catch(e) {}
  }, []);

  // ── Initial document HTML ─────────────────────────────────────────────────
  const buildInitialHTML = () => {
    const leftColHtml = leftDetails.map(d => `
      <div style="margin-bottom:8px;">
        <span style="font-weight:600;color:#1a1a1a;">${d.label}:</span>
        <span style="color:#333;margin-left:8px;">${d.value}</span>
      </div>`).join('');

    const rightColHtml = rightDetails.map(d => `
      <div style="margin-bottom:8px;">
        <span style="font-weight:600;color:#1a1a1a;">${d.label}:</span>
        <span style="color:#333;margin-left:8px;">${d.value}</span>
      </div>`).join('');

    return `
      <div style="font-family:Calibri,sans-serif;">
        
       
      </div>`;
  };

  useEffect(() => {
    console.log(studyId,'study ids')
    if (!studyId) return;

    const loadReport = async () => {
        try {
            const res = await axios.get(
                `${API_BASE_URL}/api/reports/default/${studyId}/`
            );
              const report = res.data
              if (report) {
                setCurrentReportId(report.report.id);
                setCurrentReportName(report.report.report_name);
            
                if (editorRef.current) {
                    editorRef.current.innerHTML =
                        report.report.report_text || buildInitialHTML();
                }
            
                if (res.data.study_status === "signed_off") {
                    setReportStatus(STATUS.SIGNED_OFF);
                } else if (res.data.study_status === "locked") {
                    setReportStatus(STATUS.LOCKED);
                } else {
                    setReportStatus(STATUS.OPEN);
                }
            } else {
                if (editorRef.current) {
                    editorRef.current.innerHTML = buildInitialHTML();
                }
            
                setReportStatus(
                    res.data.study_status === "signed_off"
                        ? STATUS.SIGNED_OFF
                        : STATUS.OPEN
                );
            }
        }
        catch (err) {
            console.error(err);
        }
    };

    loadReport();

}, [studyId]);
  const saveReport = async () => {
    try {
        let reportId = currentReportId;
        if (!reportId) {
            const createResponse = await axios.post(
                `${API_BASE_URL}/api/reports/create/`,
                {
                    study_id: studyId,
                    format_id: selectedFormat,
                    report_name: currentReportName,
                }
            );
               
            reportId = createResponse.data.report.id;
            setCurrentReportId(reportId);
        }
        console.log( {report_id: reportId,
          report_name: currentReportName,
          report_text: editorRef.current.innerHTML,
          status: "Draft"})
        // Save report
        await axios.post(
            `${API_BASE_URL}/api/reports/save/`,
            {
                report_id: reportId,
                report_name: currentReportName,
                report_text: editorRef.current.innerHTML,
                status: "Draft",
            }
        );
        handleSaveAndLock();
    } catch (error) {
        console.error(error);
        alert("Failed to save report.");
    }
  };
const openReport = (report) => {
  setCurrentReportId(report.id);
  setCurrentReportName(report.report_name);
  if (report.format_id || report.format) {
    setSelectedFormat(String(report.format_id || report.format));
  }
  if (report.status === "Draft" || report.status === "open") {
    setReportStatus(STATUS.OPEN);
  } else if (report.status === "Locked") {
    setReportStatus(STATUS.LOCKED);
  } else if (report.status === "Signed Off") {
    setReportStatus(STATUS.SIGNED_OFF);
  }

  if (editorRef.current) {
      editorRef.current.innerHTML = report.report_text || "";
  }
  
  bottomToolbarRef.current?.closeManageModal();
};

  const handleSaveAndLock = async () => {
    if (reportStatus === STATUS.SIGNED_OFF) {
      setStatusMsg("⛔ Signed-off reports cannot be re-saved.");
      return;
    }
    setLoading(true);
    setStatusMsg("💾 Saving...");
    try {
      await generateWordReport(p, editorRef.current?.innerHTML || "", STATUS.LOCKED);
      await updateStudyStatus(STATUS.LOCKED);
      setReportStatus(STATUS.LOCKED);
      setStatusMsg("✅ Saved and locked.");
      setTimeout(() => setStatusMsg(""), 3000);
    } catch(e) {
      console.error(e);
      setError("Error saving — npm install docx file-saver");
      setStatusMsg("❌ Save failed.");
    } finally {
      setLoading(false);
    }
  };

  // Unlock
  const handleUnlock = async () => {
    if (reportStatus !== STATUS.LOCKED) return;
    if (!window.confirm("Unlock report for editing?")) return;
    setIsUpdatingStatus(true);
    try {
      await updateStudyStatus(STATUS.OPEN);
      setReportStatus(STATUS.OPEN);
      setStatusMsg("🔓 Unlocked for editing.");
      setTimeout(() => setStatusMsg(""), 3000);
    } catch {
      setReportStatus(STATUS.OPEN);
      setStatusMsg("🔓 Unlocked locally.");
      setTimeout(() => setStatusMsg(""), 3000);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Sign Off
  const handleSignOff = async () => {
    if (reportStatus !== STATUS.LOCKED) {
      setStatusMsg("⚠ Save and lock the report before signing off.");
      setTimeout(() => setStatusMsg(""), 3000);
      return;
    }
    if (!window.confirm("Sign off this report? This cannot be undone.")) return;
    setIsUpdatingStatus(true);
    setStatusMsg("✍ Signing off...");
    try {
      await updateStudyStatus(STATUS.SIGNED_OFF);
      setReportStatus(STATUS.SIGNED_OFF);
      setStatusMsg("✅ Report signed off.");
      setTimeout(() => setStatusMsg(""), 3000);
    } catch {
      setReportStatus(STATUS.SIGNED_OFF);
      setStatusMsg("✅ Signed off locally.");
      setTimeout(() => setStatusMsg(""), 3000);
    } finally {
      setIsUpdatingStatus(false);
    }
  };
  const handleDownloadPrint = (type) => {
    switch(type){
        case "print":
            printReport();
            break;
        case "pdf":
            downloadPDF();
            break;
        case "pdf-images":
            downloadPDFWithImages();
            break;
        case "word":
            downloadWord();
            break;
    }
};

const downloadPDFWithImages = async () => {
  downloadPDF();
};

const printReport = () => {
  const printContents = document.getElementById("reportDocument").outerHTML;
  const popup = window.open("", "_blank");
  popup.document.write(`
      <html><head><title>Report</title>
      <style>

          body{
              font-family:Arial;
              padding:20px;
          }
          table{
              width:100%;
              border-collapse:collapse;
          }
          td{
              padding:6px;
          }
      </style>
      </head>
      <body>
          ${printContents}
      </body>
      </html>
  `);

  popup.document.close();
  const images = popup.document.images;

  if (images.length === 0) {
    popup.focus();
    popup.print();
    popup.close();
    return;
  }

  let loaded = 0;

  const printWhenReady = () => {
    loaded++;
    if (loaded === images.length) {
      popup.focus();
      popup.print();
      popup.close();
    }
  };

  Array.from(images).forEach((img) => {
    if (img.complete) {
      printWhenReady();
    } else {
      img.onload = printWhenReady;
      img.onerror = printWhenReady;
    }
  });

};

const downloadPDF = async () => {
  const element =  document.getElementById("reportDocument");
  const canvas =
      await html2canvas(element, {
        useCORS: true,
          scale:2
      });
  const img = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p","mm","a4");
  const width = pdf.internal.pageSize.getWidth();
  const height = canvas.height * width / canvas.width;
  pdf.addImage(
      img,
      "PNG",
      0,
      0,
      width,
      height
  );
  pdf.save(`${currentReportName}.pdf`);

};
const downloadWord = async () => {
  
  const loggedInUserString = localStorage.getItem('user'); 
  const loggedInUser = JSON.parse(loggedInUserString);
  let temp = document.createElement("div");
 const reportText = editorRef.current?.innerHTML || "";
  generateWordReport(patient,reportText);
  

};

  const FONTS     = ["Calibri","Arial","Times New Roman","Courier New","Georgia","Verdana","Tahoma","Comic Sans MS"];
  const SIZES     = ["8","9","10","11","12","14","16","18","20","24","28","36","48","72"];
  const COLORS    = ["#000000","#c00000","#ff0000","#ff7700","#ffff00","#00b050","#0070c0","#7030a0","#ffffff","#d6d6d6","#888888","#4a4a4a"];
  const HIGHLIGHTS= ["#ffff00","#00ff00","#00ffff","#ff00ff","#0000ff","#ff0000","#ffffff","#000000","transparent"];
  const TABS      = ["Home","Insert","Design","Layout","References","Mailings","Review","View"];

  const handleFontName = (val) => { setFontName(val); exec("fontName", val); };
  const handleFontSize = (val) => {
    setFontSize(val);
    const sz = parseInt(val);
    const map = [[8,1],[10,2],[12,3],[14,4],[18,5],[24,6],[36,7]];
    const closest = map.reduce((a,b) => Math.abs(b[0]-sz) < Math.abs(a[0]-sz) ? b : a);
    exec("fontSize", sz > 36 ? 7 : closest[1]);
  };

  // Derived
  const isLocked    = reportStatus === STATUS.LOCKED;
  const isSignedOff = reportStatus === STATUS.SIGNED_OFF;
  const isEditable = reportStatus !== STATUS.LOCKED && reportStatus !== STATUS.SIGNED_OFF;
  const sStyle      = STATUS_STYLES[reportStatus] || STATUS_STYLES.open;

  return (
    <div
      className={`word-overlay ${isFullscreen ? "fullscreen" : ""}`}
      onClick={() => { setShowColorPicker(false); setShowHLPicker(false); }}
    >
      
      <div className={`word-window ${isFullscreen ? "fullscreen" : ""}`} onClick={e => e.stopPropagation()}>

        {/* ── Title bar ───────────────────────────────────────────────────── */}
        <div className="word-titlebar">
          <div className="word-titlebar-left">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="#2b579a" stroke="#fff" strokeWidth="0.5"/>
              <polyline points="14 2 14 8 20 8" fill="none" stroke="#fff" strokeWidth="1.5"/>
              <line x1="9" y1="13" x2="15" y2="13" stroke="#fff" strokeWidth="1.5"/>
              <line x1="9" y1="17" x2="15" y2="17" stroke="#fff" strokeWidth="1.5"/>
            </svg>
            <span className="word-titlebar-text">Radiology Report.docx — Word</span>

            {/* ── Status Badge ── */}
            <span style={{ background: sStyle.bg, color: sStyle.color, border: `1px solid ${sStyle.border}`, borderRadius: "4px", padding: "1px 9px", fontSize: "11px", fontWeight: 700, marginLeft: "10px", display: "inline-flex", alignItems: "center", gap: "4px", letterSpacing: "0.3px" }}>
              {sStyle.icon} {STATUS_LABELS[reportStatus]}
            </span>
          </div>

          <div className="word-titlebar-controls">
            <button className="word-win-ctrl" onClick={() => setIsFullscreen(v => !v)} title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
              {isFullscreen ? "⤬" : "⛶"}
            </button>
            <button className="word-win-ctrl" onClick={onClose} title="Close">✕</button>
          </div>
        </div>

        {/* ── Quick access toolbar ─────────────────────────────────────────── */}
        <div className="word-qat">
          {[
            { title:"Save & Lock", icon:"💾", fn: handleSaveAndLock },
            // { title:"Print",       icon:"🖨", fn: handlePrint       },
            { title:"Undo",        icon:"↩", fn: ()=>exec("undo")  },
            { title:"Redo",        icon:"↪", fn: ()=>exec("redo")  },
          ].map(b => (
            <button key={b.title} title={b.title} onClick={b.fn} className="word-qat-btn">
              {b.icon}
            </button>
          ))}
          <span className="word-qat-sep">|</span>
          <span className="word-qat-docname">Document1 — Word</span>

          {/* Inline status message in QAT */}
          {statusMsg && (
            <span style={{ fontSize:"11px", marginLeft:"10px", color: isSignedOff ? "#0d47a1" : isLocked ? "#e65100" : "#2e7d32", fontWeight:600 }}>
              {statusMsg}
            </span>
          )}
        </div>

        {/* ── Ribbon tabs ──────────────────────────────────────────────────── */}
        <div className="word-ribbon-tabbar">
          {TABS.map(t => (
            <div key={t} onClick={() => setActiveTab(t)} className={`word-ribbon-tab ${activeTab===t ? "active" : ""}`}>
              {t}
            </div>
          ))}
        </div>

        {/* ── Ribbon panel ─────────────────────────────────────────────────── */}
        <div className="word-ribbon-panel">
          {activeTab === "Home" && (
            <div className="word-ribbon-row">

              {/* Clipboard */}
              <RGroup label="Clipboard">
                <RBtn title="Paste (Ctrl+V)" onClick={()=>exec("paste")} wide>
                  <span className="word-paste-icon">
                    <span className="word-icon-large">📋</span>
                    <span className="word-icon-label">Paste</span>
                  </span>
                </RBtn>
                <div className="word-stack">
                  <RBtn title="Cut (Ctrl+X)"  onClick={()=>exec("cut")}>✂ Cut</RBtn>
                  <RBtn title="Copy (Ctrl+C)" onClick={()=>exec("copy")}>⎘ Copy</RBtn>
                  <RBtn title="Format Painter" onClick={()=>{}}>🖌 Format Painter</RBtn>
                </div>
              </RGroup>

              {/* Font */}
              <RGroup label="Font">
                <div className="word-font-group">
                  <div className="word-font-row">
                    <select value={fontName} onChange={e=>handleFontName(e.target.value)} onMouseDown={saveSelection} className="word-select word-select-font">
                      {FONTS.map(f=><option key={f} value={f} style={{fontFamily:f}}>{f}</option>)}
                    </select>
                    <select value={fontSize} onChange={e=>handleFontSize(e.target.value)} onMouseDown={saveSelection} className="word-select word-select-size">
                      {SIZES.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                    <RBtn title="Grow Font"           onClick={()=>handleFontSize(String(Math.min(72,parseInt(fontSize)+1)))}><b>A</b>↑</RBtn>
                    <RBtn title="Shrink Font"         onClick={()=>handleFontSize(String(Math.max(6,parseInt(fontSize)-1)))}><b>A</b>↓</RBtn>
                    <RBtn title="Clear All Formatting" onClick={()=>exec("removeFormat")}>
                      <span className="word-clear-format"><b>A</b><span className="word-clear-x">✕</span></span>
                    </RBtn>
                  </div>
                  <div className="word-font-row">
                    <RBtn title="Bold (Ctrl+B)"      onClick={()=>exec("bold")}          active={fmt.bold}>     <b>B</b></RBtn>
                    <RBtn title="Italic (Ctrl+I)"    onClick={()=>exec("italic")}        active={fmt.italic}>   <i>I</i></RBtn>
                    <RBtn title="Underline (Ctrl+U)" onClick={()=>exec("underline")}     active={fmt.underline}><u>U</u></RBtn>
                    <RBtn title="Strikethrough"      onClick={()=>exec("strikeThrough")} active={fmt.strike}>   <s>S</s></RBtn>
                    <RBtn title="Subscript"          onClick={()=>exec("subscript")}>    X<sub>2</sub></RBtn>
                    <RBtn title="Superscript"        onClick={()=>exec("superscript")}>  X<sup>2</sup></RBtn>
                    <Sep/>
                    {/* Text Color */}
                    <div className="word-color-picker-container">
                      <RBtn title="Font Color" onClick={()=>{saveSelection();setShowColorPicker(v=>!v);setShowHLPicker(false);}}>
                        <span className="word-color-preview">
                          <span className="word-color-letter">A</span>
                          <span className="word-color-bar" style={{background:textColor}}/>
                        </span>
                      </RBtn>
                      {showColorPicker && (
                        <div className="word-color-pop" onClick={e=>e.stopPropagation()}>
                          <div className="word-color-pop-title">Theme Colors</div>
                          <div className="word-color-grid">
                            {COLORS.map(c=>(
                              <div key={c} title={c} onClick={()=>{setTextColor(c);restoreSelection();exec("foreColor",c);setShowColorPicker(false);}} className="word-color-swatch" style={{background:c}}/>
                            ))}
                          </div>
                          <div className="word-color-custom">
                            <label className="word-color-custom-label">
                              Custom: <input type="color" value={textColor} onChange={e=>{setTextColor(e.target.value);restoreSelection();exec("foreColor",e.target.value);}} className="word-color-input"/>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Highlight */}
                    <div className="word-color-picker-container">
                      <RBtn title="Highlight" onClick={()=>{saveSelection();setShowHLPicker(v=>!v);setShowColorPicker(false);}}>
                        <span className="word-highlight-preview">
                          <span className="word-highlight-letter">ab</span>
                          <span className="word-highlight-bar" style={{background:hlColor}}/>
                        </span>
                      </RBtn>
                      {showHLPicker && (
                        <div className="word-color-pop" onClick={e=>e.stopPropagation()}>
                          <div className="word-color-pop-title">Highlight Colors</div>
                          <div className="word-color-grid">
                            {HIGHLIGHTS.map(c=>(
                              <div key={c} title={c==="transparent"?"No Color":c}
                                onClick={()=>{setHlColor(c);restoreSelection();if(c==="transparent") exec("removeFormat");else exec("backColor",c);setShowHLPicker(false);}}
                                className="word-color-swatch"
                                style={{background:c,display:"flex",alignItems:"center",justifyContent:"center"}}>
                                {c==="transparent"&&<span className="word-clear-x-sm">✕</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </RGroup>

              {/* Paragraph */}
              <RGroup label="Paragraph">
                <div className="word-para-group">
                  <div className="word-para-row">
                    <RBtn title="Bullets"    onClick={()=>exec("insertUnorderedList")} active={fmt.ul}>
                      <svg viewBox="0 0 16 14" width="16" height="14"><circle cx="2" cy="4" r="1.5" fill="#333"/><line x1="6" y1="4" x2="15" y2="4" stroke="#333" strokeWidth="1.5"/><circle cx="2" cy="10" r="1.5" fill="#333"/><line x1="6" y1="10" x2="15" y2="10" stroke="#333" strokeWidth="1.5"/></svg>
                    </RBtn>
                    <RBtn title="Numbering" onClick={()=>exec("insertOrderedList")}   active={fmt.ol}>
                      <svg viewBox="0 0 16 14" width="16" height="14"><text x="0" y="7" fontSize="6" fill="#333">1.</text><line x1="6" y1="4" x2="15" y2="4" stroke="#333" strokeWidth="1.5"/><text x="0" y="13" fontSize="6" fill="#333">2.</text><line x1="6" y1="10" x2="15" y2="10" stroke="#333" strokeWidth="1.5"/></svg>
                    </RBtn>
                    <RBtn title="Outdent" onClick={()=>exec("outdent")}>
                      <svg viewBox="0 0 16 14" width="16" height="14" fill="none" stroke="#333" strokeWidth="1.5"><line x1="1" y1="3" x2="15" y2="3"/><line x1="1" y1="7" x2="11" y2="7"/><line x1="1" y1="11" x2="15" y2="11"/><polyline points="9,5 6,7 9,9" fill="none"/></svg>
                    </RBtn>
                    <RBtn title="Indent" onClick={()=>exec("indent")}>
                      <svg viewBox="0 0 16 14" width="16" height="14" fill="none" stroke="#333" strokeWidth="1.5"><line x1="1" y1="3" x2="15" y2="3"/><line x1="5" y1="7" x2="15" y2="7"/><line x1="1" y1="11" x2="15" y2="11"/><polyline points="1,5 4,7 1,9" fill="none"/></svg>
                    </RBtn>
                    <Sep/>
                    <RBtn title="Line Spacing" onClick={()=>{}}>
                      <svg viewBox="0 0 16 14" width="16" height="14" fill="none" stroke="#333" strokeWidth="1.5"><line x1="5" y1="3" x2="15" y2="3"/><line x1="5" y1="7" x2="15" y2="7"/><line x1="5" y1="11" x2="15" y2="11"/><polyline points="1,2 3,0 3,14" fill="none" strokeWidth="1"/></svg>
                    </RBtn>
                    <RBtn title="Shading / Fill" onClick={()=>{}}>
                      <svg viewBox="0 0 16 14" width="16" height="14"><rect x="1" y="1" width="14" height="12" fill="#d6e4f7" stroke="#888" strokeWidth="1"/><line x1="4" y1="7" x2="12" y2="7" stroke="#888" strokeWidth="1" strokeDasharray="2"/></svg>
                    </RBtn>
                    <RBtn title="Borders" onClick={()=>{}}>
                      <svg viewBox="0 0 16 14" width="16" height="14" fill="none" stroke="#333" strokeWidth="1.5"><rect x="1" y="1" width="14" height="12"/><line x1="1" y1="7" x2="15" y2="7"/><line x1="8" y1="1" x2="8" y2="13"/></svg>
                    </RBtn>
                  </div>
                  <div className="word-para-row">
                    <RBtn title="Align Left" onClick={()=>exec("justifyLeft")}   active={fmt.jL}>
                      <svg viewBox="0 0 16 14" width="16" height="14" fill="none" stroke="#333" strokeWidth="1.5"><line x1="1" y1="2" x2="15" y2="2"/><line x1="1" y1="6" x2="10" y2="6"/><line x1="1" y1="10" x2="15" y2="10"/></svg>
                    </RBtn>
                    <RBtn title="Center" onClick={()=>exec("justifyCenter")} active={fmt.jC}>
                      <svg viewBox="0 0 16 14" width="16" height="14" fill="none" stroke="#333" strokeWidth="1.5"><line x1="1" y1="2" x2="15" y2="2"/><line x1="3" y1="6" x2="13" y2="6"/><line x1="1" y1="10" x2="15" y2="10"/></svg>
                    </RBtn>
                    <RBtn title="Align Right" onClick={()=>exec("justifyRight")} active={fmt.jR}>
                      <svg viewBox="0 0 16 14" width="16" height="14" fill="none" stroke="#333" strokeWidth="1.5"><line x1="1" y1="2" x2="15" y2="2"/><line x1="6" y1="6" x2="15" y2="6"/><line x1="1" y1="10" x2="15" y2="10"/></svg>
                    </RBtn>
                    <RBtn title="Justify" onClick={()=>exec("justifyFull")} active={fmt.jF}>
                      <svg viewBox="0 0 16 14" width="16" height="14" fill="none" stroke="#333" strokeWidth="1.5"><line x1="1" y1="2" x2="15" y2="2"/><line x1="1" y1="6" x2="15" y2="6"/><line x1="1" y1="10" x2="15" y2="10"/></svg>
                    </RBtn>
                  </div>
                </div>
              </RGroup>

              {/* Styles */}
              <RGroup label="Styles">
                <div className="word-styles-group">
                  {[
                    {label:"Normal",     tag:"p",   fs:11, fw:"normal", clr:"#1a1a1a"},
                    {label:"No Spacing", tag:"div",  fs:11, fw:"normal", clr:"#1a1a1a"},
                    {label:"Heading 1",  tag:"h1",   fs:16, fw:"bold",   clr:"#2b579a"},
                    {label:"Heading 2",  tag:"h2",   fs:14, fw:"bold",   clr:"#2b579a"},
                    {label:"Heading 3",  tag:"h3",   fs:12, fw:"bold",   clr:"#2b579a"},
                  ].map(s=>(
                    <button key={s.label} onMouseDown={saveSelection} onClick={()=>exec("formatBlock",s.tag)} className="word-style-btn" style={{fontSize:s.fs-2,fontWeight:s.fw,color:s.clr}}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </RGroup>

              {/* Editing */}
              <RGroup label="Editing">
                <div className="word-stack">
                  <RBtn title="Find (Ctrl+F)" onClick={()=>{}} wide>🔍 Find</RBtn>
                  <RBtn title="Replace"       onClick={()=>{}} wide>🔄 Replace</RBtn>
                  <RBtn title="Select All"    onClick={()=>exec("selectAll")} wide>☰ Select</RBtn>
                </div>
              </RGroup>

              <Sep/>

              {/* ── Export / Status actions ───────────────────────────────── */}
              <RGroup>
                <div className="word-export-group" style={{display:"flex",flexDirection:"column",gap:"4px"}}>

                  {/* Row 2: Unlock  |  Sign Off */}
                  <div style={{display:"flex",gap:"4px"}}>
                    {/* Unlock — only when locked */}
                    {isLocked && (
                      <button
                        onClick={handleUnlock}
                        disabled={isUpdatingStatus}
                        title="Unlock report to resume editing"
                        style={{
                          background:   "#fff3e0",
                          color:        "#e65100",
                          border:       "1px solid #ffcc80",
                          borderRadius: "4px",
                          padding:      "4px 10px",
                          cursor:       "pointer",
                          fontWeight:   700,
                          fontSize:     "12px",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        🔓 Unlock
                      </button>
                    )}
                  </div>
                </div>
              </RGroup>

            </div>
          )}
      <div className="word-editor-container" style={{ flex: 1, overflowY: "auto", background: "#eaeaea", padding: "20px 0" }}>
         
        </div>
          {activeTab !== "Home" && (
            <div className="word-placeholder">
              "{activeTab}" tab commands — typing and Home tab formatting is fully functional.
            </div>
          )}
        </div>

        {/* ── Locked / Signed-off banners ──────────────────────────────────── */}
        {isLocked && (
          <div style={{background:"#fff8e1",borderLeft:"4px solid #ffa000",padding:"6px 14px",fontSize:"12px",color:"#5d4037",display:"flex",alignItems:"center",gap:"6px"}}>
            🔒 Report is locked. Click <strong>Unlock</strong> to edit, or <strong>Sign Off</strong> to finalize.
          </div>
        )}
        {isSignedOff && (
          <div style={{background:"#e3f2fd",borderLeft:"4px solid #1565c0",padding:"6px 14px",fontSize:"12px",color:"#0d47a1",display:"flex",alignItems:"center",gap:"6px"}}>
            ✅ Report signed off by radiologist. This document is read-only.
          </div>
        )}

        {/* ── Ruler ────────────────────────────────────────────────────────── */}
        <div className="word-ruler">
          <div className="word-ruler-inner">
            {Array.from({length:18},(_,i)=>(
              <div key={i} className={`word-ruler-tick ${i%2===0 ? "major" : "minor"}`} style={{left:`${i*5.56}%`}}>
                <div className="word-ruler-tick-line"/>
                {i%2===0&&i>0&&<span className="word-ruler-tick-label">{i/2}</span>}
              </div>
            ))}
          </div>
        </div>

        {/* ── Document area ─────────────────────────────────────────────────── */}
        <div className="word-doc-area">
          <div className="word-page" style={{transform:`scale(${zoom/100})`}}>
            <div
              ref={editorRef}
              data-word-editor="true"
              contentEditable={isEditable}
              suppressContentEditableWarning
              onKeyUp={refreshFmt}
              onMouseUp={refreshFmt}
              onSelect={() => { saveSelection(); refreshFmt(); }}
              onBlur={saveSelection}
              className="word-editor"
              spellCheck
              style={
                isLocked || isSignedOff
                  ? { background:"#f9f9f9", color:"#555", cursor:"not-allowed", userSelect:"text" }
                  : {}
              }
            />
          </div>
        </div>

        {/* ── Status bar ────────────────────────────────────────────────────── */}
        <div className="word-statusbar">
          <div className="word-statusbar-left">
            <span>Page 1 of 1</span>
            <span>Words: {wordCount}</span>
            <span className={error ? "word-error" : ""}>{error||"English (India) ✔"}</span>
          </div>
          <div className="word-statusbar-right">
            {/* Status chip in status bar */}
            <span
              style={{
                background:   sStyle.bg,
                color:        sStyle.color,
                border:       `1px solid ${sStyle.border}`,
                borderRadius: "3px",
                padding:      "1px 7px",
                fontSize:     "11px",
                fontWeight:   700,
                marginRight:  "8px",
              }}
            >
              {sStyle.icon} {STATUS_LABELS[reportStatus]}
            </span>
            <span>📝 {isEditable ? "Editing Mode" : isLocked ? "Locked" : "Read-Only"}</span>
            <span className="word-status-sep">|</span>
            <span style={{fontSize:11}}>Zoom</span>
            <input type="range" min={50} max={150} value={zoom} onChange={e=>setZoom(+e.target.value)} className="word-zoom-slider"/>
            <span style={{fontSize:11,minWidth:34}}>{zoom}%</span>
          </div>
        </div>
       
        <ReportingBottomToolbar
         ref={bottomToolbarRef}
          studyId={studyId}
          closeModel = {closeModel}
          reportId={currentReportId}
          reportName={currentReportName}
          onReportNameChange={setCurrentReportName}
          onNewReportCreated={handleNewReportCreated}
          onSaveReport={saveReport}
          onDownloadPrint={handleDownloadPrint}
          onSignOff={handleSignOff}
          onViewRecordings={() => console.log("recordings")}
          formats={formats}
          selectedFormat={selectedFormat}
          onTemplateChange={handleTemplateChange}
          onOpenReport = {openReport}
        />
        <div style={{position: "absolute",left: "-9999px",top: 0}}>
          <ReportDocument
              patient={patient}
              doctor={localStorage.getItem('user')}
              reportText={editorRef.current?.innerHTML || ""}
          />
        </div>
      </div>
    </div>
  );
}